import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { formatDateTime } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";
import BriefCustomer from "../components/etams/BriefCustomer";
import { labelPaket, labelProduk } from "../lib/faKonfigurasi";

// LAYAR REVIEW SPV — 29 Agustus 2026.
//
// KENAPA LAYAR INI LAHIR. K55 menyebut gerbang SPV KERAS: desainer nol boleh
// mengirim ke customer tanpa persetujuan SPV. Diukur 29 Agustus: gerbangnya
// punya KONSUMEN sejak 22 Agustus (`_ditolak_spv`), produsennya baru lahir
// hari ini, dan LAYARNYA nol pernah ada — `design_iterations` bahkan nol
// punya satu pun endpoint GET sampai hari ini.
//
// Gerbang yang nol bisa DILIHAT adalah gerbang yang nol bisa dijaga,
// seberapa pun benar endpoint vonisnya.
//
// ⛔ KOREKSI 31 Agustus 2026 — paragraf lama di sini menulis "`submit-final`
//    masih terbuka, dan itu disengaja". SUDAH TIDAK BENAR sejak 30 Agustus:
//    `design_tasks_extras.py` memanggil `lolos_spv()` sebelum submit-final,
//    jadi gerbangnya HIDUP. Komentar yang menceritakan keadaan lama nyaris
//    membuat pembacanya percaya gerbang belum ada — kelas cacat yang sama
//    dengan blok SPV di CLAUDE.md yang dikoreksi 29 Agustus.
//
//    ⛔ KALIMAT DI SINI DULU BERBUNYI "desainer nol punya tombol kirim-ke-SPV
//    di UI, jadi alur desainer lewat aplikasi BUNTU". Itu SUDAH TIDAK BENAR
//    dan dicabut 5 September 2026: `DesignTaskDetail.jsx` punya
//    `doKirimKeSpv()` yang memanggil `POST /design-iterations/submit`, dan ia
//    terikat `<Button onClick={doKirimKeSpv}>` — diukur, bukan disimpulkan.
//
// ⚠ Ironi yang layak dicatat: blok di ATAS ini ada justru untuk memperingatkan
//   bahwa komentar yang bertahan sesudah sebabnya hilang membuat pembacanya
//   percaya sesuatu belum ada. Lalu komentar itu sendiri mengalaminya.
//   Peringatan nol kebal terhadap penyakit yang diperingatkannya.

export default function SpvReview() {
  const { profile } = useAuth();
  const role = profile?.role?.code;
  const bolehVonis = role === "SPV_DESAINER";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [masukan, setMasukan] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/design-iterations/antrean-spv");
      setItems(r.data?.items || []);
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal memuat antrean review."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vonis = async (it, decision) => {
    const teks = (masukan[it.id] || "").trim();
    // Dicegat DI SINI supaya SPV nol kehilangan apa yang sudah diketik ke
    // galat server. Pagarnya sendiri tetap di router DAN di CHECK tabelnya —
    // tiga lapis, dan yang ini cuma yang paling sopan.
    if (decision === "REJECTED" && teks.length < 3) {
      toast.error("Penolakan wajib disertai masukan untuk desainer (min. 3 karakter).");
      return;
    }
    setBusy(it.id);
    try {
      await api.post(`/design-iterations/${it.id}/spv-review`, {
        decision, feedback: teks || null,
      });
      toast.success(decision === "APPROVED"
        ? "Desain disetujui — boleh dikirim ke customer."
        : "Desain dikembalikan ke desainer beserta masukanmu.");
      setMasukan((m) => ({ ...m, [it.id]: "" }));
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan keputusan."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="spv-review-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          Review Desain
          <Hint>Menolak tidak memotong jatah revisi customer — ini pemeriksaan internal.</Hint>
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Kiriman desain yang menunggu penilaianmu sebelum sampai ke customer.
        </p>
      </div>

      {!bolehVonis && (
        // Owner boleh MEMBACA antrean — pengawasan butuh melihat yang
        // menumpuk. Ia nol boleh memvonis, dan itu ditegakkan CHECK tabelnya,
        // bukan cuma disembunyikan di sini.
        <div className="flex items-start gap-2 rounded border border-info/40
                        bg-info-soft text-info-soft-foreground p-3 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Kamu hanya bisa membaca antrean ini; yang memberi persetujuan hanya SPV Desainer.
          </span>
        </div>
      )}

      {loading && <div className="text-muted-foreground text-sm">Memuat…</div>}
      {err && (
        <div className="flex items-start gap-2 rounded border border-danger/40
                        bg-danger-soft text-danger-soft-foreground p-3 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {!loading && !err && items.length === 0 && (
        <Card className="p-6 text-sm text-muted-foreground">
          Belum ada kiriman desain yang menunggu review.
        </Card>
      )}

      {items.map((it) => (
        <Card key={it.id} className="p-4 space-y-3" data-testid="spv-antrean-item">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-semibold">{it.dr_number || "Pesanan"}</span>
            <span className="text-sm text-muted-foreground" data-testid="spv-jenis-kiriman">
              {it.iteration_number > 1 ? `Perbaikan ke-${it.iteration_number - 1} dari customer` : "Desain pertama"}
            </span>
            <span className="text-xs text-muted-foreground">
              dikirim {formatDateTime(it.created_at)}
            </span>
            {it.product_summary_type && (
              <span className="text-xs text-muted-foreground">
                {labelProduk(it.product_summary_type)}{it.package_type ? ` · Paket ${labelPaket(it.package_type)}` : ""}
              </span>
            )}
          </div>

          {/* T51: perbaikan SPV menempel ke kiriman yang SAMA (K56 poin 2) — tanda waktunya ditulis jelas. */}
          {it.perbaikan_masuk_pada && (
            <div className="rounded border border-info/40 bg-info-soft text-info-soft-foreground p-2 text-sm"
                 data-testid="spv-perbaikan-masuk">
              Perbaikan desainer masuk {formatDateTime(it.perbaikan_masuk_pada)} — berkas bertanda “baru” di bawah.
            </div>
          )}

          {it.masukan_customer_sebelumnya && (
            <div className="text-sm"><span className="text-muted-foreground">Customer minta diperbaiki: </span>
              “{it.masukan_customer_sebelumnya}”</div>
          )}
          {it.catatan_pd && (
            <div className="text-sm"><span className="text-muted-foreground">Catatan pesanan: </span>{it.catatan_pd}</div>
          )}
          {it.notes && <div className="text-sm"><span className="text-muted-foreground">Catatan desainer: </span>{it.notes}</div>}

          {/* T13 (PRIORITAS TINGGI): berkas kiriman + permintaan customer — dulu kartu tanpa gambar, gerbang K55 buta. */}
          <BerkasKiriman berkas={it.berkas || []} />
          <details className="rounded border border-border p-3" data-testid="spv-brief" open>
            <summary className="cursor-pointer text-sm font-medium">Permintaan customer</summary>
            <div className="pt-3"><BriefCustomer drId={it.design_request_id} /></div>
          </details>

          {(it.riwayat_vonis || []).length > 0 && (
            // Riwayat ronde ditampilkan, bukan disembunyikan: SPV perlu tahu
            // apa yang ia MINTA sebelumnya untuk menilai apakah sudah dipenuhi.
            <div className="rounded border border-warning/40 bg-warning-soft
                            text-warning-soft-foreground p-3 text-sm space-y-1" data-testid="spv-riwayat">
              {it.riwayat_vonis.map((r) => (
                <div key={r.round_number}>
                  <span className="font-medium">
                    Penilaian ke-{r.round_number} · {r.decision === "REJECTED" ? "dikembalikan" : "disetujui"}
                  </span>
                  <span className="text-xs"> · {formatDateTime(r.reviewed_at)}</span>
                  {r.feedback && <div>Masukan: “{r.feedback}”</div>}
                </div>
              ))}
            </div>
          )}

          {/* T50: sesudah dikembalikan, penilaian berikutnya menunggu perbaikan desainer (backend 409 juga). */}
          {it.menunggu_perbaikan && (
            <div className="rounded border border-border bg-muted p-3 text-sm" data-testid="spv-menunggu-perbaikan">
              Menunggu perbaikan desainer. Tombol penilaian terbuka lagi sesudah desainer mengirim ulang.
            </div>
          )}

          {bolehVonis && !it.menunggu_perbaikan && (
            <>
              <Textarea
                data-testid="spv-masukan"
                placeholder="Masukan untuk desainer — wajib kalau dikembalikan, boleh kosong kalau disetujui."
                value={masukan[it.id] || ""}
                onChange={(e) => setMasukan((m) => ({ ...m, [it.id]: e.target.value }))}
                rows={2}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="spv-setujui"
                  disabled={busy === it.id}
                  onClick={() => vonis(it, "APPROVED")}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Setujui — boleh ke customer
                </Button>
                <Button
                  variant="destructive"
                  data-testid="spv-tolak"
                  disabled={busy === it.id}
                  onClick={() => vonis(it, "REJECTED")}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Kembalikan ke desainer
                </Button>
              </div>
            </>
          )}
        </Card>
      ))}

      <KlasifikasiRevisi bolehVonis={bolehVonis} />
    </div>
  );
}

/* K59 butir 1 / K190 butir 2 (BUSINESS-10): SPV menetapkan ALASAN revisi
   BERIKUTNYA untuk PD yang customernya minta revisi. `KESALAHAN_ETAMS` = tidak
   memakan jatah customer. Tanpa klasifikasi ini desainer tidak bisa mengirim
   revisi (409 MENUNGGU_KLASIFIKASI_SPV) — dulu tidak ada pintunya di layar. */
/* Kode alasan → kalimat untuk layar. Kode yang belum ada di daftar (daftar belum termuat / alasan sudah tidak aktif)
   TIDAK ditampilkan mentah — cukup "tercatat". */
function labelAlasan(daftar, kode) {
  const ketemu = (daftar || []).find((a) => a.code === kode);
  return ketemu?.label_id || "tercatat";
}

function KlasifikasiRevisi({ bolehVonis }) {
  const [antrean, setAntrean] = useState([]);
  const [alasan, setAlasan] = useState([]);
  const [pilih, setPilih] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/design-iterations/antrean-klasifikasi"),
        api.get("/design-iterations/alasan-revisi"),
      ]);
      setAntrean(a.data?.items || []);
      setAlasan(b.data?.items || []);
      setErr(null);
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal memuat antrean klasifikasi."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const simpan = async (dr) => {
    const kode = pilih[dr.id] || dr.alasan_kode;
    if (!kode) { toast.error("Pilih alasan revisi dulu."); return; }
    setBusy(dr.id);
    try {
      const r = await api.post(`/design-iterations/klasifikasi/${dr.id}`, { alasan_kode: kode });
      toast.success(r.data?.memakan_jatah
        ? `Alasan "${r.data.label}" — revisi ini MEMAKAN jatah customer.`
        : `Alasan "${r.data.label}" — revisi ini TIDAK memakan jatah customer.`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan klasifikasi."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3 pt-4" data-testid="spv-klasifikasi">
      <div>
        <h2 className="text-lg font-semibold">
          Klasifikasi alasan revisi{" "}
          {/* T53 (26 Okt 2026): jatah revisi customer = dasar 3, sekali bayar membuka ke-4 sampai ke-6 (K75);
              Kesalahan ETAMS tidak memakan jatah (K59 butir 1, Q6). Angka "6×" dulu bertentangan dengan layar
              customer "3 dari 3". */}
          <Hint>Tetapkan alasannya sebelum desainer mengirim perbaikan. Pilih "Kesalahan ETAMS" kalau perbaikan ini karena kekeliruan tim kita — perbaikan itu tidak mengurangi jatah revisi customer (jatah dasar 3 kali, bisa dibuka sampai 6 kali dengan sekali bayar).</Hint>
        </h2>
        <p className="text-muted-foreground text-sm mt-1">Pesanan yang customernya sedang minta perbaikan desain.</p>
      </div>
      {err && <div className="text-sm text-danger-soft-foreground">{err}</div>}
      {antrean.length === 0 && !err && (
        <Card className="p-4 text-sm text-muted-foreground">Tidak ada PD yang menunggu klasifikasi.</Card>
      )}
      {antrean.map((dr) => (
        <Card key={dr.id} className="p-4 flex flex-wrap items-center gap-3" data-testid="spv-klasifikasi-item">
          <span className="font-semibold">{dr.dr_number || "—"}</span>
          <span className="text-xs text-muted-foreground">perbaikan ke-{dr.perbaikan_ke ?? ((dr.revision_count ?? 0) + 1)}</span>
          {dr.sudah_diklasifikasi && (
            /* SPV-KODE-ALASAN-MENTAH-01 (K200): label dari daftar alasan, bukan kode mentah. */
            <span className="text-xs rounded px-2 py-0.5 bg-success-soft text-success-soft-foreground"
                  data-testid="spv-klasifikasi-sudah">
              Alasan sudah dipilih: {labelAlasan(alasan, dr.alasan_kode)}
            </span>
          )}
          {bolehVonis && (
            <>
              <select
                className="border rounded px-2 py-1 text-sm bg-background"
                value={pilih[dr.id] ?? dr.alasan_kode ?? ""}
                onChange={(e) => setPilih((m) => ({ ...m, [dr.id]: e.target.value }))}
                data-testid={`spv-klasifikasi-pilih-${dr.dr_number || dr.id}`}
              >
                <option value="">— pilih alasan —</option>
                {alasan.map((a) => <option key={a.code} value={a.code}>{a.label_id}</option>)}
              </select>
              <Button size="sm" disabled={busy === dr.id} onClick={() => simpan(dr)}
                      data-testid={`spv-klasifikasi-simpan-${dr.dr_number || dr.id}`}>
                Simpan alasan
              </Button>
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

/* T13 (26 Okt 2026): berkas satu kiriman — gambar tampil langsung (URL bertanda tangan, dimuat saat kartu tampil),
   berkas lain (PDF, PSD, CDR, ZIP) lewat tombol Buka. "baru" = diunggah sesudah penilaian "dikembalikan" terakhir. */
const JENIS_GAMBAR = ["image/jpeg", "image/png", "image/webp"];

function BerkasKiriman({ berkas }) {
  const [url, setUrl] = useState({});
  const [gagal, setGagal] = useState({});
  useEffect(() => {
    let hidup = true;
    berkas.filter((f) => JENIS_GAMBAR.includes(f.file_mime_type)).forEach((f) => {
      api.get(`/design-files/${f.id}/signed-url`)
        .then((r) => { if (hidup && r.data?.signed_url) setUrl((m) => ({ ...m, [f.id]: r.data.signed_url })); })
        .catch((e) => { if (hidup) setGagal((m) => ({ ...m, [f.id]: apiErrorMessage(e, "Gambar gagal dimuat.") })); });
    });
    return () => { hidup = false; };
  }, [berkas]);

  const buka = async (f) => {
    try {
      const r = await api.get(`/design-files/${f.id}/signed-url`);
      if (r.data?.signed_url) window.open(r.data.signed_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Berkas gagal dibuka."));
    }
  };

  if (berkas.length === 0) {
    return <div className="text-sm text-muted-foreground italic">Kiriman ini belum punya berkas.</div>;
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="spv-berkas">
      {berkas.map((f) => (
        <div key={f.id} className="rounded border border-border p-2 space-y-1">
          {url[f.id] ? (
            <button type="button" onClick={() => buka(f)} className="block w-full" title="Buka ukuran penuh">
              <img src={url[f.id]} alt={f.file_filename} className="h-40 w-full object-contain bg-muted rounded" />
            </button>
          ) : (
            <div className="h-40 w-full flex items-center justify-center bg-muted rounded text-xs text-muted-foreground">
              {gagal[f.id] || (JENIS_GAMBAR.includes(f.file_mime_type) ? "Memuat gambar…" : "Bukan gambar — tekan Buka")}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="truncate" title={f.file_filename}>{f.file_filename}</span>
            {f.baru && <span className="shrink-0 rounded bg-info-soft text-info-soft-foreground px-1.5">baru</span>}
          </div>
          <button type="button" onClick={() => buka(f)} className="text-xs text-info hover:underline">Buka</button>
        </div>
      ))}
    </div>
  );
}
